import { type TouchEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './banner-slider.css';

export interface BannerLink {
  id: number | string;
  src: string;
  alt: string;
  href?: string;
  to?: string;
}

export interface BannerSliderProps {
  slides: BannerLink[];
  className?: string;
}

export interface BannerListProps {
  slides: BannerLink[];
  className?: string;
}

const FALLBACK_SLIDE: BannerLink = { id: 'fallback', src: '/images/banner.png', alt: '메인 배너' };
const ROLL_INTERVAL_MS = 4000;
const RESUME_DELAY_MS = 3000;
const SWIPE_THRESHOLD_PX = 50;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function BannerImage({ slide, inert }: { slide: BannerLink; inert?: boolean }) {
  const tabIndex = inert ? -1 : undefined;
  const img = <img src={slide.src} alt={slide.alt} />;

  if (slide.href) {
    return (
      <a href={slide.href} target="_blank" rel="noopener noreferrer" tabIndex={tabIndex}>
        {img}
      </a>
    );
  }
  if (slide.to) {
    return (
      <Link to={slide.to} tabIndex={tabIndex}>
        {img}
      </Link>
    );
  }
  return img;
}

export function BannerSlider({ slides, className }: BannerSliderProps) {
  const effectiveSlides = slides.length > 0 ? slides : [FALLBACK_SLIDE];
  const total = effectiveSlides.length;
  const [currentIndex, setCurrentIndex] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoveringRef = useRef(false);
  const focusedRef = useRef(false);
  const touchStartXRef = useRef<number | null>(null);

  function clearTimers() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
  }

  function startRolling() {
    if (total <= 1 || prefersReducedMotion()) return;
    clearTimers();
    intervalRef.current = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % total);
    }, ROLL_INTERVAL_MS);
  }

  function pauseRolling() {
    clearTimers();
  }

  function resumeRolling(delay: number) {
    if (total <= 1) return;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    if (delay === 0) {
      startRolling();
      return;
    }
    resumeTimeoutRef.current = setTimeout(() => {
      resumeTimeoutRef.current = null;
      if (!hoveringRef.current && !focusedRef.current) startRolling();
    }, delay);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: startRolling/clearTimers는 total을 클로저로 읽는다 — 재구독은 total 변화로만 통제한다.
  useEffect(() => {
    setCurrentIndex(0);
    if (!hoveringRef.current && !focusedRef.current) startRolling();
    return () => clearTimers();
  }, [total]);

  function handleDotClick(index: number) {
    setCurrentIndex(index);
    pauseRolling();
    resumeRolling(RESUME_DELAY_MS);
  }

  function handleMouseEnter() {
    hoveringRef.current = true;
    pauseRolling();
  }
  function handleMouseLeave() {
    hoveringRef.current = false;
    if (!focusedRef.current) resumeRolling(0);
  }
  function handleFocus() {
    focusedRef.current = true;
    pauseRolling();
  }
  function handleBlur() {
    focusedRef.current = false;
    if (!hoveringRef.current) resumeRolling(0);
  }

  function handleTouchStart(e: TouchEvent<HTMLElement>) {
    if (total <= 1) return;
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
    pauseRolling();
  }
  function handleTouchEnd(e: TouchEvent<HTMLElement>) {
    const startX = touchStartXRef.current;
    if (startX === null) return;
    touchStartXRef.current = null;
    const endX = e.changedTouches[0]?.clientX ?? startX;
    const deltaX = startX - endX;
    if (Math.abs(deltaX) > SWIPE_THRESHOLD_PX) {
      setCurrentIndex((i) => (deltaX > 0 ? (i + 1) % total : (i - 1 + total) % total));
    }
    resumeRolling(RESUME_DELAY_MS);
  }

  return (
    <section
      className={['banner-slider', className].filter(Boolean).join(' ')}
      aria-roledescription="carousel"
      aria-label="배너"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="banner-slider__track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {effectiveSlides.map((slide, index) => {
          const active = index === currentIndex;
          return (
            // biome-ignore lint/a11y/useSemanticElements: ARIA APG 캐러셀 슬라이드 패턴(role="group") — fieldset은 폼 그룹 시맨틱이라 맞지 않는다.
            <div
              key={slide.id}
              className={`banner-slider__slide${active ? ' banner-slider__slide--active' : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${total}`}
              aria-hidden={active ? undefined : true}
            >
              <figure className="banner-slider__figure">
                <BannerImage slide={slide} inert={!active} />
              </figure>
            </div>
          );
        })}
      </div>
      {total > 1 && (
        <div className="banner-slider__dots">
          {effectiveSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`banner-slider__dot${index === currentIndex ? ' banner-slider__dot--active' : ''}`}
              aria-label={`${index + 1}번째 배너로 이동`}
              aria-current={index === currentIndex ? true : undefined}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function BannerList({ slides, className }: BannerListProps) {
  if (slides.length === 0) return null;

  return (
    <div className={['banner-list', className].filter(Boolean).join(' ')}>
      {slides.map((slide) => (
        <figure key={slide.id} className="banner-list__figure">
          <BannerImage slide={slide} />
        </figure>
      ))}
    </div>
  );
}
